import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@postdaily/ui/components/alert";
import { Button, buttonVariants } from "@postdaily/ui/components/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@postdaily/ui/components/empty";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { cn } from "@postdaily/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeftIcon, PenLineIcon } from "lucide-react";
import {
  parseAsArrayOf,
  parseAsString,
  parseAsStringEnum,
  useQueryStates,
} from "nuqs";

import { ClientContainer } from "@/calendar/components/client-container";
import { CalendarProvider } from "@/calendar/contexts/calendar-context";
import type { TCalendarView } from "@/calendar/types";
import { ChannelAvatar } from "@/components/channels/channel-avatar";
import { useComposer } from "@/components/composer/composer-dialog-provider";
import { PageHeader } from "@/components/page-header";
import {
  type PublishLayout,
  STATUS_BUCKETS,
  type StatusBucket,
  statusesForBucket,
} from "@/components/publish/filters";
import {
  PublishFilterBar,
  PublishLayoutToggle,
} from "@/components/publish/publish-filter-bar";
import { PublishListView } from "@/components/publish/publish-list-view";
import { resolveDefaultTimezone } from "@/components/scheduling/schedule-time";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { track } from "@/lib/analytics";
import { getChannelLabel } from "@/lib/channel-label";
import { getProviderLabel } from "@/lib/provider-labels";
import { useTRPC } from "@/utils/trpc";

const CALENDAR_VIEWS: TCalendarView[] = [
  "day",
  "week",
  "month",
  "year",
  "agenda",
];

export const Route = createFileRoute("/(app)/channels/$id/publish")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { currentWorkspace } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return <Skeleton className="h-96 w-full" />;
  }

  return <ChannelPublish workspaceId={currentWorkspace.id} accountId={id} />;
}

function ChannelPublish({
  workspaceId,
  accountId,
}: {
  workspaceId: string;
  accountId: string;
}) {
  const trpc = useTRPC();
  const { openComposer } = useComposer();
  const { currentWorkspace } = useCurrentWorkspace();
  // URL-persisted like the global hub, so refresh/back/deep links keep the
  // layout, sub-view, status tab, and tag filter.
  const [filters, setFilters] = useQueryStates({
    layout: parseAsStringEnum<PublishLayout>(["list", "calendar"]).withDefault(
      "calendar",
    ),
    status: parseAsStringEnum<StatusBucket>([...STATUS_BUCKETS]).withDefault(
      "scheduled",
    ),
    tags: parseAsArrayOf(parseAsString).withDefault([]),
    view: parseAsStringEnum(CALENDAR_VIEWS).withDefault("week"),
  });
  const view = filters.view;
  const setView = (next: TCalendarView) => void setFilters({ view: next });
  const layout = filters.layout;
  // The toggle reports a click on the already-active layout too.
  const setLayout = (next: PublishLayout) => {
    if (next === layout) {
      return;
    }

    track("publish_layout_changed", { layout: next });
    void setFilters({ layout: next });
  };
  const statusBucket = filters.status;
  const setStatusBucket = (next: StatusBucket) =>
    void setFilters({ status: next });
  const tagIds = filters.tags;
  const setTagIds = (next: string[]) => void setFilters({ tags: next });
  const displayTimezone = resolveDefaultTimezone(
    currentWorkspace?.defaultTimezone,
  );
  const accountsQuery = useQuery(
    trpc.socialAccounts.list.queryOptions({ workspaceId }),
  );
  const account = accountsQuery.data?.find((item) => item.id === accountId);

  if (accountsQuery.isPending) {
    return <Skeleton className="h-96 w-full" />;
  }

  if (accountsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Channel unavailable</AlertTitle>
        <AlertDescription>{accountsQuery.error.message}</AlertDescription>
      </Alert>
    );
  }

  if (!account) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Channel not found" />
        <Alert>
          <AlertTitle>Channel not found</AlertTitle>
          <AlertDescription>
            This channel may have been disconnected.
          </AlertDescription>
        </Alert>
        <Link
          to="/channels"
          className={buttonVariants({ variant: "outline", size: "sm" })}
        >
          <ArrowLeftIcon data-icon="inline-start" />
          All channels
        </Link>
      </div>
    );
  }

  const name = getChannelLabel(account);
  const handle = account.providerAccountHandle;
  const showDraftsOnCalendarNotice =
    layout === "calendar" && statusBucket === "drafts";
  const fillViewport = layout === "calendar" && !showDraftsOnCalendarNotice;

  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        fillViewport && "min-h-0 flex-1 gap-3 overflow-y-auto",
      )}
      {...(fillViewport ? { "data-calendar-fill": "" } : {})}
    >
      <PageHeader
        leading={
          <ChannelAvatar
            layout="overlay"
            name={name}
            avatarUrl={account.avatarUrl}
            provider={account.provider}
            size="lg"
          />
        }
        title={name}
        description={`${getProviderLabel(account.provider)}${handle ? ` · ${handle}` : ""}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PublishLayoutToggle value={layout} onChange={setLayout} />
            <Button type="button" size="sm" onClick={() => openComposer()}>
              <PenLineIcon data-icon="inline-start" />
              New post
            </Button>
          </div>
        }
        mobileTrailing={
          <Button type="button" size="sm" onClick={() => openComposer()}>
            <PenLineIcon data-icon="inline-start" />
            New
          </Button>
        }
      />

      <div className="shrink-0">
        <PublishFilterBar
          status={statusBucket}
          onStatusChange={setStatusBucket}
          tagIds={tagIds}
          onTagIdsChange={setTagIds}
          showChannelFilter={false}
          countAccountIds={[accountId]}
        />
      </div>

      {layout === "list" ? (
        <PublishListView
          workspaceId={workspaceId}
          statuses={statusesForBucket(statusBucket)}
          tagIds={tagIds}
          accountIds={[accountId]}
          bucket={statusBucket}
          displayTimezone={displayTimezone}
        />
      ) : showDraftsOnCalendarNotice ? (
        <Empty className="rounded-xl border">
          <EmptyHeader>
            <EmptyTitle>Drafts aren’t scheduled</EmptyTitle>
            <EmptyDescription>
              Drafts have no scheduled time, so they don’t appear on the
              calendar. Switch to the List view to see them.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button variant="outline" onClick={() => setLayout("list")}>
              Switch to List
            </Button>
          </EmptyContent>
        </Empty>
      ) : (
        // Keyed by account so switching between channels' Publish views remounts
        // a fresh, channel-locked calendar. No channel filter — already scoped.
        // Drill-downs (month day cell → day view) stay on this page by only
        // switching the local sub-view, never navigating to the global hub.
        <CalendarProvider
          key={accountId}
          initialSelectedAccountIds={[accountId]}
          timezone={displayTimezone}
          onDrillDown={(_date, nextView) => setView(nextView)}
        >
          <ClientContainer
            view={view}
            onSelectView={setView}
            accountIds={[accountId]}
            statuses={statusesForBucket(statusBucket)}
            tagIds={tagIds}
          />
        </CalendarProvider>
      )}
    </div>
  );
}
